import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Eraser, ImagePlus, Loader2, Lock, MessageSquarePlus, Pencil, Send, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { compressImage } from '../lib/imageCompression';
import { useAuth } from '../contexts/AuthContext';
import ModalPortal from './ModalPortal';
import { laatsteKliks } from '../lib/gebruiksEvents';
import {
  isMeldingNogAanpasbaar,
  softwareLijstService,
  type EigenMeldingWijziging,
  type MeldingErnst,
  type MeldingSoort,
} from '@/features/software-lijst';
import {
  DuidelijkheidPaneel,
  LEGE_VELDEN,
  MeldingVelden,
  STATUS_LABEL_MELDER,
  effectieveStatus,
  isFormulierCompleet,
  ontbrekendeVelden,
  stappenUitVelden,
  stelOmschrijvingSamen,
  useMeldingDuidelijkheid,
  MeldingFeedbackKaart,
  MeldingTijdlijnCompact,
  type MeldingVeldenWaarden,
} from '@/features/meldingen';

/**
 * Meldpunt software (topbar, alle interne accounts): een probleem of
 * aanpassingsverzoek doorgeven aan Tom — mét context (verzoek Tom 25-08):
 * automatische schermafbeelding op het moment van klikken, de exacte URL,
 * de laatste scriptfouten en gerichte vragen (wat deed je, hoe erg is het).
 * Eerste steen van de feedback-machine (B4/B21).
 */

// Foutbuffer: de laatste scriptfouten reizen mee met de melding, zodat de
// technische oorzaak vaak al bekend is zonder naspelen.
const foutBuffer: { tijd: string; melding: string }[] = [];
function onthoudFout(melding: string) {
  foutBuffer.push({ tijd: new Date().toISOString(), melding: melding.slice(0, 300) });
  if (foutBuffer.length > 5) foutBuffer.shift();
}
if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) =>
    onthoudFout(String(e.message ?? e.error ?? 'onbekende fout')),
  );
  window.addEventListener('unhandledrejection', (e) =>
    onthoudFout(`unhandled: ${String((e as PromiseRejectionEvent).reason).slice(0, 280)}`),
  );
}

/**
 * Schermafbeelding van het zichtbare deel van het scherm, met drie engines
 * als vangnet en een harde tijdslimiet per poging.
 *
 * Geleerd van twee incidenten, dus niet terugdraaien:
 *  - Alleen de viewport, nooit de volledige scrollhoogte (#574): oudere
 *    WebKit weigert een canvas boven ~16,7 mln pixels met 'The object is in
 *    an invalid state', nieuwere levert stilzwijgend een leeg 'data:,'.
 *    #581 haalde die uitsnede per ongeluk weg, waardoor Safari weer faalde.
 *  - Een tijdslimiet per engine: modern-screenshot deed op een lange pagina
 *    al 6-14 s op een lichte testpagina en de knop wachtte daar zonder
 *    limiet op — daardoor kwam het meldvenster op Safari nooit meer open en
 *    zijn er tussen 29-08 en 10-09 nul meldingen ingestuurd.
 * De meldingsmodal zelf ([data-melding-venster]) wordt uit het beeld
 * gefilterd: de afbeelding wordt ná het openen van het venster gemaakt.
 */
const SNAPSHOT_TIJDSLIMIET_MS = 6_000;

function metTijdslimiet<T>(maak: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    maak,
    new Promise<never>((_, weiger) =>
      setTimeout(() => weiger(new Error(`tijdslimiet ${ms / 1000}s`)), ms),
    ),
  ]);
}

async function maakSchermafbeelding(): Promise<{ url: string | null; fout: string | null }> {
  const schaal = Math.min(1, 1600 / window.innerWidth);
  const x = window.scrollX;
  const y = window.scrollY;
  const breed = window.innerWidth;
  const hoog = window.innerHeight;
  const inVenster = (el: Element) => el.closest?.('[data-melding-venster]') != null;
  // Pas laden bij een melding: houdt beide engines uit de hoofdbundel.
  const [{ default: html2canvas }, { domToJpeg }] = await Promise.all([
    import('html2canvas'),
    import('modern-screenshot'),
  ]);

  const pogingen: Array<() => Promise<string>> = [
    // 1 · html2canvas volledig. 2 · idem maar zonder <img>/<canvas>/<svg>
    // (grafiek-canvassen en vreemde afbeeldingen zijn de gebruikelijke
    // boosdoeners). 3 · modern-screenshot als laatste redmiddel.
    ...([false, true] as const).map((kaal) => async () => {
      const canvas = await html2canvas(document.body, {
        x,
        y,
        width: breed,
        height: hoog,
        scale: schaal,
        logging: false,
        useCORS: true,
        imageTimeout: 3000,
        ignoreElements: (el) =>
          el.tagName === 'IFRAME' ||
          el.tagName === 'VIDEO' ||
          inVenster(el) ||
          (kaal &&
            (el.tagName === 'IMG' ||
              el.tagName === 'CANVAS' ||
              el.tagName.toLowerCase() === 'svg')),
      });
      if (!canvas.width || !canvas.height) {
        throw new Error(`canvas bleef leeg (${canvas.width}×${canvas.height})`);
      }
      const url = canvas.toDataURL('image/jpeg', 0.8);
      // Boven zijn grens (of bij geheugendruk) gooit WebKit niet altijd,
      // maar antwoordt stilletjes 'data:,'.
      if (url.length < 2000) throw new Error(`afbeelding bleef leeg (${url.length} tekens)`);
      return url;
    }),
    async () => {
      const url = await domToJpeg(document.body, {
        quality: 0.8,
        scale: schaal,
        width: breed,
        height: hoog,
        // De body een stukje opschuiven binnen het vaste kader = uitsnede
        // op de huidige scrollpositie.
        style: { transform: `translate(${-x}px, ${-y}px)`, transformOrigin: 'top left' },
        filter: (node) =>
          !(
            node instanceof Element &&
            (node.tagName === 'IFRAME' || node.tagName === 'VIDEO' || inVenster(node))
          ),
      });
      if (url.length < 2000) throw new Error(`afbeelding bleef leeg (${url.length} tekens)`);
      return url;
    },
  ];

  const foutjes: string[] = [];
  for (const poging of pogingen) {
    try {
      return { url: await metTijdslimiet(poging(), SNAPSHOT_TIJDSLIMIET_MS), fout: null };
    } catch (err) {
      foutjes.push(String((err as Error)?.message ?? err).slice(0, 120));
    }
  }
  console.error('Schermafbeelding mislukt:', foutjes);
  return { url: null, fout: foutjes.join(' | ').slice(0, 200) || 'onbekend' };
}

/**
 * Eigen afbeelding (bibliotheek of camera, verzoek Pieter 30-09) klaarzetten
 * zoals de automatische schermafbeelding: één JPEG-data-URL van max. 1600 px
 * breed, zodat de tekenlaag en de upload er niets van merken. Via een canvas,
 * zodat ook PNG/HEIC van een iPhone als echte JPEG het pad `.jpg` ingaat.
 */
const EIGEN_AFBEELDING_MAX_PX = 1600;

async function bestandNaarJpegDataUrl(bestand: File): Promise<string> {
  const klein = await compressImage(bestand);
  const objectUrl = URL.createObjectURL(klein);
  try {
    const img = await new Promise<HTMLImageElement>((klaar, weiger) => {
      const el = new Image();
      el.onload = () => klaar(el);
      el.onerror = () => weiger(new Error('Afbeelding kon niet gelezen worden'));
      el.src = objectUrl;
    });
    const schaal = Math.min(1, EIGEN_AFBEELDING_MAX_PX / Math.max(img.naturalWidth, 1));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.naturalWidth * schaal));
    canvas.height = Math.max(1, Math.round(img.naturalHeight * schaal));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas niet beschikbaar');
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    const url = canvas.toDataURL('image/jpeg', 0.85);
    if (url.length < 2000) throw new Error('Afbeelding bleef leeg');
    return url;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

interface EigenMelding {
  id: string;
  soort: string;
  omschrijving: string;
  stappen: string | null;
  ernst: string | null;
  scherm: string | null;
  status: string;
  beoordeling: string | null;
  beoordeeld_op: string | null;
  /** Bij opgelost: wat is er aangepast en hoe werkt het. */
  uitleg: string | null;
  /** Terugmelding van de melder (werkt/werkt_niet/begrepen/niet_begrepen); null = nog gevraagd. */
  feedback: string | null;
  doorvraag: unknown;
  created_at: string;
}

const SOORT_OPTIES: ReadonlyArray<readonly [MeldingSoort, string]> = [
  ['probleem', 'Iets werkt niet goed'],
  ['verzoek', 'Aanpassing / wens'],
];

const ERNST_OPTIES: ReadonlyArray<readonly [MeldingErnst, string]> = [
  ['blokkeert', 'Blokkeert mijn werk'],
  ['hinderlijk', 'Hinderlijk'],
  ['klein', 'Kleinigheid'],
];

function SoortKeuze({
  waarde,
  onKies,
}: {
  waarde: MeldingSoort;
  onKies: (s: MeldingSoort) => void;
}) {
  return (
    <div className="flex gap-1.5">
      {SOORT_OPTIES.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onKies(key)}
          className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
            waarde === key
              ? 'bg-brand/10 text-brand'
              : 'bg-surface-secondary text-foreground-secondary hover:text-foreground'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

function ErnstKeuze({
  waarde,
  onKies,
}: {
  waarde: MeldingErnst | null;
  onKies: (e: MeldingErnst | null) => void;
}) {
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <span className="text-xs font-medium text-foreground-secondary mr-1">Hoe erg is het?</span>
      {ERNST_OPTIES.map(([key, label]) => (
        <button
          key={key}
          type="button"
          onClick={() => onKies(waarde === key ? null : key)}
          className={`rounded-lg px-2.5 py-1.5 text-xs font-medium transition-colors ${
            waarde === key
              ? key === 'blokkeert'
                ? 'bg-error/10 text-error'
                : 'bg-brand/10 text-brand'
              : 'bg-surface-secondary text-foreground-secondary hover:text-foreground'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

const INVOER_KLASSE =
  'mt-1 w-full px-3 py-2 text-sm bg-surface border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/40 resize-y font-normal';

/**
 * Bewerkformulier voor een eigen, nog onbeoordeelde melding (verzoek Pieter
 * 30-09: hij had per ongeluk "probleem" gekozen in plaats van "wens").
 */
function EigenMeldingBewerken({
  melding,
  onKlaar,
}: {
  melding: EigenMelding;
  onKlaar: () => void;
}) {
  const queryClient = useQueryClient();
  const [velden, setVelden] = useState<EigenMeldingWijziging>({
    soort: melding.soort === 'verzoek' ? 'verzoek' : 'probleem',
    omschrijving: melding.omschrijving,
    stappen: melding.stappen ?? '',
    ernst: (melding.ernst as MeldingErnst | null) ?? null,
  });
  const [bezig, setBezig] = useState(false);

  const opslaan = async () => {
    setBezig(true);
    try {
      await softwareLijstService.bewerkEigenMelding(melding.id, velden);
      toast.success('Melding aangepast.');
      queryClient.invalidateQueries({ queryKey: ['software_meldingen'] });
      onKlaar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Aanpassen mislukt');
    } finally {
      setBezig(false);
    }
  };

  return (
    <div className="space-y-2.5 pt-1">
      <SoortKeuze waarde={velden.soort} onKies={(soort) => setVelden((v) => ({ ...v, soort }))} />
      <label className="block text-xs font-medium text-foreground-secondary">
        {velden.soort === 'probleem' ? 'Wat gaat er mis?' : 'Wat zou je willen?'}
        <textarea
          value={velden.omschrijving}
          onChange={(e) => setVelden((v) => ({ ...v, omschrijving: e.target.value }))}
          rows={3}
          className={INVOER_KLASSE}
        />
      </label>
      {velden.soort === 'probleem' && (
        <>
          <label className="block text-xs font-medium text-foreground-secondary">
            Wat deed je net?
            <textarea
              value={velden.stappen}
              onChange={(e) => setVelden((v) => ({ ...v, stappen: e.target.value }))}
              rows={2}
              className={INVOER_KLASSE}
            />
          </label>
          <ErnstKeuze
            waarde={velden.ernst}
            onKies={(ernst) => setVelden((v) => ({ ...v, ernst }))}
          />
        </>
      )}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onKlaar}
          disabled={bezig}
          className="rounded-lg px-2.5 py-1.5 text-xs font-medium text-foreground-tertiary hover:text-foreground"
        >
          Annuleren
        </button>
        <button
          type="button"
          onClick={opslaan}
          disabled={bezig}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-hover transition-colors disabled:opacity-50"
        >
          {bezig && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          Opslaan
        </button>
      </div>
    </div>
  );
}

/**
 * Tekenlaag op de schermafbeelding: met een dikke rode pen omcirkelen wat je
 * bedoelt — aanwijzen is voor niet-technische melders de duidelijkste taal.
 */
function AnnotatieCanvas({
  bron,
  onWijzig,
}: {
  bron: string;
  onWijzig: (dataUrl: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tekenen = useRef(false);
  const heeftLijnen = useRef(false);

  const laadBron = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.onload = () => {
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0);
      ctx.strokeStyle = '#e11d48';
      ctx.lineWidth = Math.max(4, Math.round(img.naturalWidth / 300));
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    };
    img.src = bron;
  };

  useEffect(laadBron, [bron]);

  const positie = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((e.clientX - rect.left) / rect.width) * canvas.width,
      y: ((e.clientY - rect.top) / rect.height) * canvas.height,
    };
  };

  return (
    <div className="space-y-1">
      <canvas
        ref={canvasRef}
        onPointerDown={(e) => {
          tekenen.current = true;
          heeftLijnen.current = true;
          const ctx = canvasRef.current!.getContext('2d')!;
          const { x, y } = positie(e);
          ctx.beginPath();
          ctx.moveTo(x, y);
          (e.target as HTMLCanvasElement).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          if (!tekenen.current) return;
          const ctx = canvasRef.current!.getContext('2d')!;
          const { x, y } = positie(e);
          ctx.lineTo(x, y);
          ctx.stroke();
        }}
        onPointerUp={() => {
          tekenen.current = false;
          onWijzig(canvasRef.current!.toDataURL('image/jpeg', 0.8));
        }}
        className="max-h-64 w-full rounded-md border border-theme-border object-contain bg-surface-secondary cursor-crosshair touch-none"
      />
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-foreground-tertiary inline-flex items-center gap-1">
          <Pencil className="h-3 w-3" /> Teken op de afbeelding wat je bedoelt (rode pen)
        </p>
        <button
          onClick={() => {
            laadBron();
            heeftLijnen.current = false;
            onWijzig(bron);
          }}
          className="inline-flex items-center gap-1 text-[11px] text-foreground-tertiary hover:text-foreground"
        >
          <Eraser className="h-3 w-3" /> Wissen
        </button>
      </div>
    </div>
  );
}

// Statuslabels (incl. 'onvolledig' en de afgeleide stand 'beantwoord') staan
// in src/features/meldingen/lib/meldingStatus.ts.
const STATUS_LABEL: Record<string, { label: string; klasse: string }> = STATUS_LABEL_MELDER;

/** Voorinvulling voor hergebruik buiten de topbar (bv. de beoordeelmodus). */
export interface MeldingVoorvul {
  /** Vooringevulde schermnaam (anders de huidige route/documenttitel). */
  scherm?: string;
  soort?: 'probleem' | 'verzoek';
  /** Herkenbaar kenmerk vóór de omschrijving, bv. "Matching-voorstel: ". */
  omschrijvingPrefix?: string;
  /** Extra context die meereist in tech_info (bv. de beoordeelde post). */
  techInfo?: Record<string, unknown>;
}

export default function MeldingKnop({
  trigger,
  voorvul,
}: {
  /** Eigen knop-render (default: de topbar-knop); krijgt de open-functie. */
  trigger?: (openModal: () => void) => React.ReactNode;
  voorvul?: MeldingVoorvul;
} = {}) {
  const { user, profile } = useAuth();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [snapshotBezig, setSnapshotBezig] = useState(false);
  const [soort, setSoort] = useState<MeldingSoort>('probleem');
  // Verplichte structuur per soort (besluit Tom 01-10); samen de omschrijving.
  const [velden, setVelden] = useState<MeldingVeldenWaarden>(LEGE_VELDEN);
  const [ernst, setErnst] = useState<MeldingErnst | null>(null);
  // AI-duidelijkheidscheck vóór het insturen, met doorvragen en uitleg.
  const duidelijkheid = useMeldingDuidelijkheid();
  const [scherm, setScherm] = useState('');
  const [screenshot, setScreenshot] = useState<string | null>(null);
  // De versie mét tekenlaag-markeringen (als de melder iets omcirkeld heeft).
  const [bewerkt, setBewerkt] = useState<string | null>(null);
  const [screenshotFout, setScreenshotFout] = useState<string | null>(null);
  const [screenshotMee, setScreenshotMee] = useState(true);
  // Zelf gekozen afbeelding (bibliotheek/camera) i.p.v. de schermafbeelding.
  const [eigenAfbeelding, setEigenAfbeelding] = useState(false);
  const [afbeeldingLaden, setAfbeeldingLaden] = useState(false);
  const bestandInputRef = useRef<HTMLInputElement>(null);
  // Eigen melding die in "Mijn meldingen" open staat om te bewerken.
  const [bewerkMeldingId, setBewerkMeldingId] = useState<string | null>(null);
  const [versturen, setVersturen] = useState(false);

  const eigen = useQuery({
    queryKey: ['software_meldingen', 'eigen'],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('software_meldingen')
        .select(
          'id, soort, omschrijving, stappen, ernst, scherm, status, beoordeling, beoordeeld_op, uitleg, feedback, doorvraag, created_at',
        )
        .eq('melder_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as EigenMelding[];
    },
  });

  // Loopt zolang de schermafbeelding op de achtergrond gemaakt wordt, zodat
  // versturen er heel even op kan wachten in plaats van zonder foto te gaan.
  const snapshotRef = useRef<Promise<{ url: string | null; fout: string | null }> | null>(null);

  const openModal = () => {
    // Het venster opent DIRECT — de schermafbeelding volgt op de achtergrond
    // en verschijnt zodra hij klaar is. De knop mag nooit meer wachten op de
    // render-engines: daar heeft hij van 29-08 tot 10-09 op gehangen, met
    // een eindeloze spinner en nul meldingen tot gevolg. Het venster zelf
    // wordt via [data-melding-venster] uit het beeld gefilterd, dus de
    // afbeelding toont nog steeds het scherm zoals de melder het zag.
    setBewerkt(null);
    setScreenshot(null);
    setScreenshotFout(null);
    setSnapshotBezig(true);
    setScherm(
      voorvul?.scherm ??
        (location.pathname === '/' || location.pathname === '/dashboard'
          ? document.title
          : location.pathname),
    );
    setVelden(LEGE_VELDEN);
    duidelijkheid.reset();
    setErnst(null);
    setSoort(voorvul?.soort ?? 'probleem');
    setScreenshotMee(true);
    setEigenAfbeelding(false);
    setAfbeeldingLaden(false);
    setBewerkMeldingId(null);
    setOpen(true);

    // Eén tel wachten zodat het venster eerst op het scherm staat: html2canvas
    // kloont de DOM synchroon en blokkeert anders de render van het venster.
    const lopend = new Promise<{ url: string | null; fout: string | null }>((klaar) => {
      setTimeout(() => klaar(maakSchermafbeelding()), 80);
    });
    snapshotRef.current = lopend;
    void lopend.then(({ url, fout }) => {
      if (snapshotRef.current !== lopend) return; // venster intussen opnieuw geopend
      setScreenshot(url);
      setScreenshotFout(fout);
      setSnapshotBezig(false);
    });
  };

  // Eigen afbeelding kiezen: vervangt de automatische schermafbeelding in
  // dezelfde tekenlaag en dezelfde upload. Geen `capture` op de input: dat is
  // een browserhint die op iPhone de bibliotheek wegneemt — zonder kan de
  // melder zelf kiezen tussen bibliotheek en camera.
  const kiesEigenAfbeelding = async (bestand: File | undefined) => {
    if (!bestand) return;
    setAfbeeldingLaden(true);
    try {
      const url = await bestandNaarJpegDataUrl(bestand);
      // Een nog lopende schermafbeelding mag de keuze niet overschrijven.
      snapshotRef.current = null;
      setSnapshotBezig(false);
      setScreenshot(url);
      setBewerkt(null);
      setScreenshotFout(null);
      setScreenshotMee(true);
      setEigenAfbeelding(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Afbeelding kon niet gelezen worden');
    } finally {
      setAfbeeldingLaden(false);
      if (bestandInputRef.current) bestandInputRef.current.value = '';
    }
  };

  /**
   * Insturen in twee stappen (besluit Tom 01-10): eerst beoordeelt de AI of
   * de melding naspeelbaar is. Onduidelijk → het paneel toont waarom en de
   * vragen; na antwoorden beoordeelt hij opnieuw (max. twee keer). Slaat de
   * melder dat over (`overslaan`), dan komt de melding binnen als 'onvolledig'.
   */
  const verstuur = async (opties: { overslaan?: boolean } = {}) => {
    if (!isFormulierCompleet(soort, velden)) {
      toast.error(`Vul eerst in: ${ontbrekendeVelden(soort, velden).join(', ').toLowerCase()}.`);
      return;
    }
    const samengesteld = stelOmschrijvingSamen(soort, velden);
    const tekst = `${voorvul?.omschrijvingPrefix ?? ''}${samengesteld}`;
    const stappen = stappenUitVelden(soort, velden) ?? '';
    const kliks = laatsteKliks();
    setVersturen(true);
    try {
      let status: 'nieuw' | 'onvolledig' = 'onvolledig';
      if (!opties.overslaan) {
        const beeld = bewerkt ?? screenshot;
        const vervolg = await duidelijkheid.beoordeel({
          soort,
          omschrijving: tekst,
          stappen: stappen || null,
          ernst: soort === 'probleem' ? ernst : null,
          scherm: scherm.trim() || null,
          url: window.location.href,
          kliks,
          fouten: foutBuffer.slice(-5),
          heeftSchermafbeelding: !!beeld && screenshotMee,
          screenshotBase64: beeld && screenshotMee ? beeld.split(',')[1] : undefined,
        });
        if (vervolg.actie === 'doorvragen') return; // paneel toont uitleg + vragen
        status = vervolg.status;
      }
      // Nog geen afbeelding maar wel eentje onderweg? Heel even meewachten
      // (nooit lang: de engines hebben zelf al een tijdslimiet), zodat een
      // snelle melder niet per ongeluk zonder foto verstuurt.
      let uitTeSturen = bewerkt ?? screenshot;
      let afbeeldingFout = screenshotFout;
      if (!uitTeSturen && snapshotBezig && snapshotRef.current) {
        const klaar = await Promise.race([
          snapshotRef.current,
          new Promise<null>((r) => setTimeout(() => r(null), 4000)),
        ]);
        if (klaar) {
          uitTeSturen = klaar.url;
          afbeeldingFout = klaar.fout;
        }
      }

      // Screenshot uploaden (als aanwezig en aangevinkt).
      let screenshotPath: string | null = null;
      if (uitTeSturen && screenshotMee) {
        const blob = await (await fetch(uitTeSturen)).blob();
        const pad = `meldingen/${user!.id}/${Date.now()}.jpg`;
        const { error: upErr } = await supabase.storage.from('documents').upload(pad, blob, {
          contentType: 'image/jpeg',
          upsert: true,
        });
        if (upErr) onthoudFout(`screenshot-upload mislukt: ${upErr.message}`.slice(0, 200));
        else screenshotPath = pad;
      }

      const { error } = await supabase.from('software_meldingen').insert({
        melder_id: user!.id,
        melder_naam: profile?.full_name ?? profile?.email ?? null,
        melder_rol: profile?.role ?? null,
        scherm: scherm.trim() || null,
        url: window.location.href,
        soort,
        omschrijving: tekst,
        stappen: stappen || null,
        ernst: soort === 'probleem' ? ernst : null,
        status,
        onvolledig_sinds: status === 'onvolledig' ? new Date().toISOString() : null,
        doorvraag: duidelijkheid.doorvraagVoorInsturen(status) as never,
        screenshot_path: screenshotPath,
        tech_info: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}×${window.innerHeight}`,
          fouten: foutBuffer.slice(-5),
          kliks,
          ...(afbeeldingFout ? { screenshot_fout: afbeeldingFout } : {}),
          ...(eigenAfbeelding && screenshotPath ? { eigen_afbeelding: true } : {}),
          ...(voorvul?.techInfo ? { context: voorvul.techInfo } : {}),
        },
      });
      if (error) throw error;
      toast.success(
        status === 'onvolledig'
          ? 'Doorgegeven als onvolledig — je vindt hem in Mijn Inbox om aan te vullen.'
          : 'Doorgegeven aan Tom — je vindt de status hier terug.',
      );
      queryClient.invalidateQueries({ queryKey: ['software_meldingen'] });
      setVelden(LEGE_VELDEN);
      duidelijkheid.reset();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Versturen mislukt');
    } finally {
      setVersturen(false);
    }
  };

  return (
    <>
      {trigger ? (
        trigger(openModal)
      ) : (
        <button
          onClick={openModal}
          title="Probleem of aanpassing in de software doorgeven"
          aria-label="Probleem of aanpassing doorgeven"
          className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
        >
          <MessageSquarePlus className="h-5 w-5" />
        </button>
      )}

      {open && (
        <ModalPortal>
          {/* z-[120]: ook bruikbaar bóven de beoordeelmodus (z-100/110). */}
          <div
            data-melding-venster
            className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-surface shadow-xl"
            >
              <div className="sticky top-0 flex items-center justify-between border-b border-theme-border bg-surface px-5 py-3.5 z-10">
                <div>
                  <h3 className="text-sm font-semibold text-foreground">
                    Software: probleem of verzoek doorgeven
                  </h3>
                  <p className="text-xs text-foreground-tertiary mt-0.5">
                    Gaat rechtstreeks naar Tom; je ziet hieronder wat ermee gebeurt.
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="rounded-lg p-1 text-foreground-tertiary hover:bg-surface-secondary hover:text-foreground"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-5 space-y-3">
                <SoortKeuze waarde={soort} onKies={setSoort} />

                <MeldingVelden soort={soort} velden={velden} onChange={setVelden} />

                {soort === 'probleem' && <ErnstKeuze waarde={ernst} onKies={setErnst} />}

                {!screenshot && snapshotBezig && (
                  <p className="flex items-center gap-1.5 text-xs text-foreground-tertiary">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Schermafbeelding wordt gemaakt — je kunt vast typen.
                  </p>
                )}
                {!screenshot && !snapshotBezig && (
                  <p className="text-xs text-foreground-tertiary">
                    Schermafbeelding kon niet gemaakt worden — de melding gaat gewoon door en de
                    technische reden reist automatisch mee.
                  </p>
                )}
                {screenshot && (
                  <div className="rounded-lg border border-theme-border p-2 space-y-1.5">
                    <label className="flex items-center gap-2 text-xs text-foreground-secondary cursor-pointer">
                      <input
                        type="checkbox"
                        checked={screenshotMee}
                        onChange={(e) => setScreenshotMee(e.target.checked)}
                      />
                      {eigenAfbeelding
                        ? 'Eigen afbeelding meesturen'
                        : 'Schermafbeelding meesturen (gemaakt op het moment dat je op de knop klikte)'}
                    </label>
                    {screenshotMee && <AnnotatieCanvas bron={screenshot} onWijzig={setBewerkt} />}
                  </div>
                )}
                <div>
                  <input
                    ref={bestandInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void kiesEigenAfbeelding(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    onClick={() => bestandInputRef.current?.click()}
                    disabled={afbeeldingLaden}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-theme-border bg-surface px-2.5 py-1.5 text-xs font-medium text-foreground-secondary hover:text-foreground hover:bg-surface-secondary transition-colors disabled:opacity-50"
                  >
                    {afbeeldingLaden ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <ImagePlus className="h-3.5 w-3.5" />
                    )}
                    {screenshot ? 'Andere afbeelding kiezen' : 'Eigen afbeelding kiezen'}
                  </button>
                  <span className="ml-2 text-[11px] text-foreground-tertiary">
                    Uit je foto's of met de camera; vervangt de schermafbeelding.
                  </span>
                </div>

                <label className="block text-xs text-foreground-secondary">
                  Scherm (automatisch ingevuld, pas aan als het ergens anders was)
                  <input
                    type="text"
                    value={scherm}
                    onChange={(e) => setScherm(e.target.value)}
                    className="mt-1 w-full px-3 py-2 text-sm bg-surface-secondary/50 border border-theme-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand/40"
                  />
                </label>

                <DuidelijkheidPaneel
                  staat={duidelijkheid.staat}
                  onAntwoord={duidelijkheid.zetAntwoord}
                  onOpnieuw={() => void verstuur()}
                  onOverslaan={() => void verstuur({ overslaan: true })}
                  bezig={versturen}
                />

                <div className="flex justify-end">
                  <button
                    onClick={() => void verstuur()}
                    disabled={versturen || duidelijkheid.staat.vragen.length > 0}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover transition-colors disabled:opacity-50"
                  >
                    {versturen ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4" />
                    )}
                    Versturen
                  </button>
                </div>

                {(eigen.data?.length ?? 0) > 0 && (
                  <div className="pt-2 border-t border-theme-border space-y-1.5">
                    <p className="text-xs font-semibold text-foreground">Mijn meldingen</p>
                    {eigen.data!.map((m) => (
                      <div key={m.id} className="rounded-lg bg-surface-secondary/50 px-3 py-2">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs text-foreground min-w-0 truncate">
                            <span className="text-foreground-tertiary">
                              {m.soort === 'verzoek' ? 'Wens' : 'Probleem'} ·{' '}
                            </span>
                            {m.omschrijving}
                          </p>
                          <span className="flex items-center gap-1.5 flex-shrink-0">
                            <span
                              className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_LABEL[effectieveStatus(m)]?.klasse ?? ''}`}
                            >
                              {STATUS_LABEL[effectieveStatus(m)]?.label ?? m.status}
                            </span>
                            {isMeldingNogAanpasbaar(m) ? (
                              <button
                                type="button"
                                onClick={() =>
                                  setBewerkMeldingId((huidig) => (huidig === m.id ? null : m.id))
                                }
                                title="Melding aanpassen (kan zolang Tom hem nog niet beoordeeld heeft)"
                                aria-label="Melding aanpassen"
                                className={`rounded-md p-1 transition-colors ${
                                  bewerkMeldingId === m.id
                                    ? 'bg-brand/10 text-brand'
                                    : 'text-foreground-tertiary hover:bg-surface-secondary hover:text-foreground'
                                }`}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            ) : (
                              <span
                                title="Al beoordeeld door Tom — aanpassen kan niet meer"
                                className="p-1 text-foreground-tertiary/60"
                              >
                                <Lock className="h-3.5 w-3.5" />
                              </span>
                            )}
                          </span>
                        </div>
                        {bewerkMeldingId === m.id && isMeldingNogAanpasbaar(m) && (
                          <EigenMeldingBewerken
                            melding={m}
                            onKlaar={() => setBewerkMeldingId(null)}
                          />
                        )}
                        {!isMeldingNogAanpasbaar(m) && !m.beoordeling && !m.uitleg && (
                          <p className="text-[11px] text-foreground-tertiary mt-1">
                            Beoordeeld — aanpassen kan niet meer.
                          </p>
                        )}
                        {m.beoordeling && (
                          <p className="text-[11px] text-foreground-secondary mt-1">
                            Tom: {m.beoordeling}
                          </p>
                        )}
                        {m.uitleg && (
                          <p className="text-[11px] text-foreground-secondary mt-1 whitespace-pre-line">
                            <span className="font-medium">Wat is aangepast:</span> {m.uitleg}
                          </p>
                        )}
                        {/* Feedbackvraag na beoordeling (besluit Tom 01-10); zelfde kaart als Mijn Inbox › Taken. */}
                        {m.feedback == null &&
                          (m.status === 'opgelost' || m.status === 'afgewezen') && (
                            <div className="mt-1.5">
                              <MeldingFeedbackKaart melding={m} compact />
                            </div>
                          )}
                        {/* Geschiedenis van de melding, zonder interne regels (besluit Tom 01-10). */}
                        <MeldingTijdlijnCompact meldingId={m.id} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </>
  );
}
